// src/pages/BarcodeDemo.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import JSZip from "jszip";
import QRCode from "qrcode";
import "bootstrap/dist/css/bootstrap.min.css";
import { apiUrl } from "../config/api";
import { toPng } from "html-to-image";

type ECLevel = "L" | "M" | "Q" | "H";

type Product = {
  code: string;
  name: string;
  price: number;
  usaPrice: number | null;
  unit?: string;
  imageUrl?: string;
};

type BatchLabel = {
  code: string;
  name: string;
  price: number | null;
};

export default function QRCodeDemo(): JSX.Element {
  // ---------- QR Inputs ----------
  const [value, setValue] = useState<string>("PD1");
  const [size, setSize] = useState<number>(256); // ขนาดภาพรวม (กว้าง x สูง)
  const [modules, setModules] = useState<number>(4); // ระยะขอบ (modules) ของตัว QR ภายใน
  const [ecLevel, setEcLevel] = useState<ECLevel>("M");

  const [priceTHB, setPriceTHB] = useState<number | null>(590);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState("");
  const [usaMode, setUsaMode] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null); 
  const batchCardRefs = useRef<Record<string, HTMLDivElement | null>>({});


  // ขอบภาพ
  const borderColor = "#111";
  const borderWidth = 2; // px

  // ---------- Products Modal ----------
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsError, setProductsError] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productQuery, setProductQuery] = useState("");
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [batchInput, setBatchInput] = useState("");
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState("");
  const [batchLabels, setBatchLabels] = useState<BatchLabel[]>([]);
  const [batchNotFound, setBatchNotFound] = useState<string[]>([]);

  async function downloadCardPNGFromDOM() {
    if (!cardRef.current) return;
  
    // pixelRatio: 2 จะคม ~600dpi ของฉลาก 50x30mm; ปรับได้ตามใจ
    const dataUrl = await toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#fff",
    });
  
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `QR_${(value || "code").replace(/\s+/g, "")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const filteredProducts = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return products;
    return products.filter(
      (p) =>
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.unit?.toLowerCase() ?? "").includes(q)
    );
  }, [products, productQuery]);

  const parseBatchCodes = (raw: string): string[] =>
    raw
      .split(/[\n,]+/)
      .map((part) => part.trim().toUpperCase())
      .filter(Boolean);

  const loadProductsCatalog = async (): Promise<Product[]> => {
    if (products.length) return products;

    setLoadingProducts(true);
    setProductsError("");
    try {
      const resp = await fetch(apiUrl("/products"));
      const data: any = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error ?? resp.statusText);
      const list: Product[] = (data.items ?? []).map((r: any) => ({
        code: String(r.code ?? ""),
        name: String(r.name ?? r.title ?? ""),
        price: Number(r.price ?? r.unit_price ?? 0),
        usaPrice: r.usaPrice !== null && r.usaPrice !== undefined ? Number(r.usaPrice) : null,
        unit: r.unit ?? r.unit_name ?? undefined,
        imageUrl: r.imageUrl ?? r.image_url ?? r.image ?? undefined,
      }));
      setProducts(list);
      return list;
    } catch (e: any) {
      const message = e?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setProductsError(message);
      throw new Error(message);
    } finally {
      setLoadingProducts(false);
    }
  };

  const openProductsModal = async () => {
    setShowProductsModal(true);
    if (products.length) return;
    setLoadingProducts(true);
    setProductsError("");
    try {
      const resp = await fetch(apiUrl("/products"));
      const data: any = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data?.error ?? resp.statusText);
      const list: Product[] = (data.items ?? []).map((r: any) => ({
        code: String(r.code ?? ""),
        name: String(r.name ?? r.title ?? ""),
        price: Number(r.price ?? r.unit_price ?? 0),
        usaPrice: r.usaPrice !== null && r.usaPrice !== undefined ? Number(r.usaPrice) : null,
        unit: r.unit ?? r.unit_name ?? undefined,
        imageUrl: r.imageUrl ?? r.image_url ?? r.image ?? undefined,
      }));
      setProducts(list);
    } catch (e: any) {
      setProductsError(e?.message ?? "โหลดรายการสินค้าไม่สำเร็จ");
    } finally {
      setLoadingProducts(false);
    }
  };
  const closeProductsModal = () => setShowProductsModal(false);

  // PMN-CRITICAL(/barcode): QR payload is the product code; USA-mode price fallback here differs from the manual price fetch path.
  const chooseProduct = (p: Product) => {
    setValue(p.code);
    const chosenPrice = usaMode
      ? (p.usaPrice !== null ? p.usaPrice : p.price)
      : p.price;
    setPriceTHB(Number.isFinite(chosenPrice) ? chosenPrice : null);
    setShowProductsModal(false);
  };

  // ---------- Canvas Preview ----------


  const priceText = useMemo(
    () => (priceTHB !== null ? `Price: ${priceTHB.toLocaleString("th-TH", { maximumFractionDigits: 0 })}.-` : ""),
    [priceTHB]
  );

  // PMN-CRITICAL(/barcode): Batch generation intentionally preserves duplicate codes so users can print multiple identical labels.
  async function generateBatchLabels() {
    const codes = parseBatchCodes(batchInput);
    if (codes.length === 0) {
      setBatchLabels([]);
      setBatchNotFound([]);
      setBatchError("กรุณาใส่รหัสสินค้าอย่างน้อย 1 รายการ");
      return;
    }

    setBatchLoading(true);
    setBatchError("");
    try {
      const catalog = await loadProductsCatalog();
      const productMap = new Map(
        catalog.map((product) => [product.code.trim().toUpperCase(), product] as const)
      );

      const nextLabels: BatchLabel[] = [];
      const nextNotFound: string[] = [];

      codes.forEach((code) => {
        const found = productMap.get(code);
        if (!found) {
          nextNotFound.push(code);
          return;
        }

        const batchPrice = usaMode
          ? (found.usaPrice !== null ? found.usaPrice : found.price)
          : found.price;
        nextLabels.push({
          code: found.code.toUpperCase(),
          name: found.name,
          price: Number.isFinite(batchPrice) ? batchPrice : null,
        });
      });

      setBatchLabels(nextLabels);
      setBatchNotFound(nextNotFound);
      if (nextLabels.length === 0) {
        setBatchError("ไม่พบรหัสสินค้าที่ใช้สร้าง QR");
      }
    } catch (e: any) {
      setBatchError(e?.message ?? "สร้างชุด QR ไม่สำเร็จ");
    } finally {
      setBatchLoading(false);
    }
  }

  function clearBatchLabels() {
    setBatchInput("");
    setBatchLabels([]);
    setBatchNotFound([]);
    setBatchError("");
  }

  async function downloadBatchPNGFromDOM() {
    if (batchLabels.length === 0) return;

    const zip = new JSZip();

    for (let index = 0; index < batchLabels.length; index += 1) {
      const item = batchLabels[index];
      const refKey = `${item.code}-${index}`;
      const node = batchCardRefs.current[refKey];
      if (!node) continue;

      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#fff",
      });

      const base64Data = dataUrl.split(",")[1];
      if (!base64Data) continue;

      zip.file(`QR_${item.code.toUpperCase()}.png`, base64Data, { base64: true });
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const zipUrl = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = zipUrl;
    a.download = "QR_BATCH.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(zipUrl);
  }



  // ---------- Downloaders ----------
  const getFileBase = () => `QR_${value || "code"}`.replace(/\s+/g, "");

// ===== helper: สร้าง SVG (เลย์เอาต์เดียวกับ QrCard) =====

function forceQRRootSize(svgStr: string, targetPx: number): string {
  // ล้าง header/doctype
  let s = svgStr
    .replace(/^<\?xml[^>]*>\s*/i, "")
    .replace(/<!DOCTYPE[^>]*>\s*/i, "");

  // บังคับให้ root <svg> มี width/height/viewBox = targetPx
  // (ไม่ใช้ style 100% เพราะภายใน PNG เราอยากได้ขนาดพิกเซลแน่นอน)
  s = s.replace(
    /<svg[^>]*>/i,
    `<svg width="${targetPx}" height="${targetPx}" viewBox="0 0 ${targetPx} ${targetPx}" preserveAspectRatio="xMidYMid meet">`
  );
  // กันเคสไม่มี </svg> ปิดด้วย
  if (!/<\/svg>\s*$/i.test(s)) s += `</svg>`;
  return s;
}
async function buildLabelSVG(
  code: string,
  price: number | null | undefined,
  ecLevel: ECLevel = "M"
): Promise<string> {
  // === หน่วยจริงเป็น mm + คำนวน px @300dpi เหมือน QrCard ===
  const mmToPx = (mm: number) => Math.round((mm / 25.4) * 300);
  const labelWmm = 50;
  const labelHmm = 30;
  const outW = mmToPx(labelWmm);
  const outH = mmToPx(labelHmm);

  // ระยะ/สัดส่วนให้เท่ากับ QrCard
  const padOuterMm = 1.8;                 // ขอบรอบฉลาก
  const gapTextMm  = 2;                   // ช่องว่างระหว่าง QR กับตัวอักษร
  const qrSideMm   = Math.max(10, labelHmm - padOuterMm * 2); // QR สูงเกือบเต็มฉลาก
  const qrPx  = mmToPx(qrSideMm);
  const qrX   = mmToPx(padOuterMm);
  const qrY   = Math.round((outH - qrPx) / 2);
  const textX = qrX + qrPx + mmToPx(gapTextMm);

  // กรอบ/มุมโค้งแบบการ์ด bootstrap rounded-3 (~.3rem) → กะบน px ของเรา
  const borderColor = "#111";
  const borderWidth = 2;                  // ให้เท่ากับที่ QrCard วาดกรอบ
  const cardRadius  = 10;                 // มุมโค้งของฉลาก
  const qrRadius    = 4;                  // มุมโค้งกรอบ QR

  // ฟอนต์ให้ตรงอารมณ์ QrCard: code หนา พอ ๆ กับ fw-bold, ราคาใหญ่แบบ fs-5
  // ใช้ dominant-baseline="middle" เพื่อจัดแนวแกน Y ให้เนียน
  const fontFamily = "system-ui, -apple-system, Segoe UI, Roboto, Arial";
  const codeSize   = 24;                  // px
  const priceSize  = 28;                  // px
  const centerY    = Math.round(outH / 2);

  // === ทำ QR เป็น SVG (ขนาดตาม qrPx) ===
  // === ทำ QR เป็น SVG ===
let qrSvgRaw = await QRCode.toString(code || "-", {
  type: "svg",
  errorCorrectionLevel: ecLevel,
  margin: 0,
  width: qrPx,                     // ให้โมดูลคมที่ 300dpi
  color: { dark: "#000", light: "#fff" },
});

// บังคับให้ root svg ของ QR มีขนาดเท่ากรอบ (แก้ปัญหา “เล็กติดมุม”)
const qrSvg = forceQRRootSize(qrSvgRaw, qrPx);

  // === SVG ทั้งใบ (ใส่มุมโค้ง + กรอบรอบ QR ให้เหมือน QrCard) ===
  const svg = `
<svg xmlns="http://www.w3.org/2000/svg"
     width="${labelWmm}mm" height="${labelHmm}mm"
     viewBox="0 0 ${outW} ${outH}">
  <style>
    text { font-family: ${fontFamily}; fill: #111; }
  </style>

  <!-- พื้นหลังการ์ด + มุมโค้งแบบ rounded -->
  <rect x="0" y="0" width="${outW}" height="${outH}" rx="${cardRadius}" ry="${cardRadius}" fill="#fff"/>

  <!-- กรอบรอบฉลาก (ให้หนาเท่า QrCard) -->
  <rect x="${borderWidth/2}" y="${borderWidth/2}"
        width="${outW - borderWidth}" height="${outH - borderWidth}"
        rx="${cardRadius - 2}" ry="${cardRadius - 2}"
        fill="none" stroke="${borderColor}" stroke-width="${borderWidth}"/>

  <!-- QR (วางซ้าย) -->
  <g transform="translate(${qrX}, ${qrY})">
    ${qrSvg}
    <!-- กรอบรอบ QR แบบมุมโค้ง -->
    <rect x="${borderWidth/2}" y="${borderWidth/2}"
          width="${qrPx - borderWidth}" height="${qrPx - borderWidth}"
          rx="${qrRadius}" ry="${qrRadius}"
          fill="none" stroke="${borderColor}" stroke-width="${borderWidth}"/>
  </g>

  <!-- ข้อความทางขวา -->
  <text x="${textX}" y="${centerY - 10}" font-size="${codeSize}" font-weight="700"
        dominant-baseline="middle">${code || "-"}</text>

  ${
    typeof price === "number"
      ? `<text x="${textX}" y="${centerY + 18}" font-size="${priceSize}" font-weight="700"
                dominant-baseline="middle">
            ${Number(price).toLocaleString("th-TH", { maximumFractionDigits: 0 })}.-</text>`
      : ""
  }
</svg>`.trim();

  return svg;
}




// ===== helper: ดึงขนาดจาก viewBox ของ SVG =====
function getViewBoxSize(svg: string): { w: number; h: number } {
  const m = svg.match(/viewBox="[^"]*?(\d+)\s+(\d+)\s+(\d+)\s+(\d+)"/i);
  if (!m) return { w: 900, h: 540 }; // fallback 50x30 mm @300dpi
  const w = parseInt(m[3], 10);
  const h = parseInt(m[4], 10);
  return { w, h };
}

// ===== action: สร้าง PNG จาก SVG แล้วดาวน์โหลด =====



  // PMN-CRITICAL(/barcode): Downloaded labels use this 50x30mm DOM card; size/modules/ecLevel inputs are not wired into this component yet.
  function QrCard({ code, price }: { code: string; price?: number | null }) {
    const [qrSvg, setQrSvg] = React.useState<string>("");
  
    // สร้าง SVG ของ QR จากค่า code (ไม่ใช้ canvas)
    React.useEffect(() => {
      const gen = async () => {
        // ---- คำนวนขนาดจริงเป็นมม./พิกเซล ----
        const mmToPx = (mm: number) => Math.round((mm / 25.4) * 300); // 300 dpi
        const labelWmm = 50;
        const labelHmm = 30;
        const padOuterMm = 1.8;   // ระยะขอบรอบ ๆ
        const qrSideMm = Math.max(10, labelHmm - padOuterMm * 2); // QR สูงเกือบเต็มฉลาก
  
        // สร้าง QR เป็น SVG (กำหนด width เป็นพิกเซล เพื่อให้รายละเอียดคม)
        const qrPx = mmToPx(qrSideMm);
        let svg = await QRCode.toString(code || "-", {
          type: "svg",
          errorCorrectionLevel: "M",
          margin: 0,
          width: qrPx,
          color: { dark: "#000", light: "#fff" },
        });
  
        // ปรับให้ svg ยืดตามกล่อง (แทนค่าคงที่ width/height)
        svg = svg
          .replace(/<\?xml[^>]*>\s*/i, "")
          .replace(/<!DOCTYPE[^>]*>\s*/i, "")
          .replace(/\swidth="[^"]*"/i, "")
          .replace(/\sheight="[^"]*"/i, "")
          .replace("<svg", '<svg style="width:100%;height:100%"');
  
        setQrSvg(svg);
      };
      gen();
    }, [code]);
  
    // ระยะต่าง ๆ (หน่วยมม.) สำหรับจัดวาง
    const labelWmm = 50;
    const labelHmm = 30;
    const padOuterMm = 1.8;
    const gapTextMm = 2;
    const qrSideMm = Math.max(10, labelHmm - padOuterMm * 2);
  
    return (
      <div className="card border rounded-3 shadow-0 " style={{ width: "50mm" }}>
        <div className="card-body px4" style={{ width: "50mm", height: "30mm" }}>
          <div className="d-flex align-items-center h-100">
            {/* กล่อง QR ทางซ้าย (ขนาดคงที่เป็นมม.) */}
            <div
              className="border rounded-1 d-flex align-items-center justify-content-center"
              style={{
                width: `${qrSideMm}mm`,
                height: `${qrSideMm}mm`,
              }}
              // ฝัง SVG ของ QR
              dangerouslySetInnerHTML={{ __html: qrSvg }}
            />
  
            {/* ช่องว่างระหว่าง QR กับข้อความ */}
            <div style={{ width: `${gapTextMm}mm` }} />
  
            {/* ข้อความทางขวา */}
            <div className="flex-grow-1">
              <div className="fw-bold">{code || "-"}</div>
              {typeof price === "number" && (
                <div className="">
                  {/* <span className="fw-semibold me-1">Price:</span> */}
                  <span className="fw-bold fs-5 text-nowrap">
                    {price.toLocaleString("th-TH", { maximumFractionDigits: 0 })}.-
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  

  // ---------- UI ----------
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4">
                <h4 className="mb-3 text-center text-success">ตัวสร้าง QR Code</h4>

                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-5">
                    <label className="form-label d-flex justify-content-between align-items-center">
                      <span>ค่าที่ต้องการเข้ารหัส</span>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={openProductsModal}
                        title="เปิดรายการสินค้าทั้งหมด"
                      >
                        ดูทั้งหมด
                      </button>
                    </label>
                    <input
                      className="form-control"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="จะใช้รหัสสินค้าหรือข้อความอื่น ๆ ก็ได้"
                    />
                  </div>

                  <div className="col-6 col-md-2">
                    <label className="form-label">ขนาด (px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={size}
                      min={160}
                      step={8}
                      onChange={(e) => setSize(Math.max(160, Math.floor(Number(e.target.value) || 160)))}
                    />
                  </div>

                  <div className="col-6 col-md-2">
                    <label className="form-label">ระยะขอบ</label>
                    <input
                      type="number"
                      className="form-control"
                      value={modules}
                      min={0}
                      max={8}
                      step={1}
                      onChange={(e) => setModules(Math.max(0, Math.min(8, Math.floor(Number(e.target.value) || 0))))}
                    />
                  </div>

                  <div className="col-12 col-md-3">
                    <label className="form-label">Error Correction</label>
                    <select className="form-select" value={ecLevel} onChange={(e) => setEcLevel(e.target.value as ECLevel)}>
                      <option value="L">L (7%)</option>
                      <option value="M">M (15%)</option>
                      <option value="Q">Q (25%)</option>
                      <option value="H">H (30%)</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
                  <button
                    type="button"
                    className={`btn btn-sm ${usaMode ? "btn-warning" : "btn-outline-secondary"}`}
                    onClick={() => {
                      setUsaMode((prev) => !prev);
                      setPriceTHB(null);
                      setPriceError("");
                    }}
                    title={usaMode ? "กำลังใช้ราคา USA — คลิกเพื่อกลับราคาปกติ" : "คลิกเพื่อใช้ราคา USA"}
                  >
                    {usaMode ? "🇺🇸 USA" : "ราคาปกติ"}
                  </button>
                  <button
                    className="btn btn-dark"
                    disabled={priceLoading}
                    onClick={async () => {
                      if (!value.trim()) return;
                      setPriceLoading(true);
                      setPriceError("");
                      try {
                        // ดึงจาก /products/price-list (หรือ fallback /products)
                        let url = new URL(apiUrl("/products/price-list"));
                        url.searchParams.set("q", value);
                        let resp = await fetch(url.toString());
                        let data: any = await resp.json().catch(() => ({}));
                        if (!resp.ok || !Array.isArray(data?.items)) {
                          resp = await fetch(apiUrl("/products"));
                          data = await resp.json().catch(() => ({}));
                          if (!resp.ok) throw new Error(data?.error ?? resp.statusText);
                        }
                        const items: any[] = Array.isArray(data?.items) ? data.items : [];
                        const found =
                          items.find((it) => String(it.code).toLowerCase() === value.toLowerCase()) ?? null;
                        if (!found) {
                          setPriceError(`ไม่พบรหัส: ${value}`);
                          setPriceTHB(null);
                        } else if (usaMode) {
                          if (found.usaPrice !== null && found.usaPrice !== undefined) {
                            setPriceTHB(found.usaPrice);
                          } else {
                            setPriceError(`สินค้า ${value} ยังไม่มีราคา USA`);
                            setPriceTHB(null);
                          }
                        } else {
                          setPriceTHB(Number(found.price ?? found.unit_price ?? 0));
                        }
                      } catch (e: any) {
                        setPriceError(e?.message ?? "โหลดราคาไม่สำเร็จ");
                      } finally {
                        setPriceLoading(false);
                      }
                    }}
                  >
                    ดึงราคา
                  </button>
                  <div className="ms-2">
                    {priceLoading && <span className="small text-muted">กำลังดึงราคา…</span>}
                    {!priceLoading && priceError && <span className="small text-danger">{priceError}</span>}
                    {!priceLoading && !priceError && priceTHB !== null && (
                      <span className="fw-bold">Price: {priceTHB.toLocaleString("th-TH", { maximumFractionDigits: 0 })}.-</span>
                    )}
                  </div>
                 
                </div>

                <hr />

                <div className="d-flex justify-content-center my-3">
                  <div ref={cardRef}>
                    <QrCard code={value} price = {priceTHB}/>
                  </div>
                </div>

                <div className="text-center d-flex gap-2 justify-content-center">
            
                  <button
                    className="btn btn-primary"
                    onClick={downloadCardPNGFromDOM}
                  >
                    ดาวน์โหลดเป็น PNG
                  </button>
                </div>

                <div className="form-text text-center mt-2">
                  มีเส้นขอบรอบภาพและรอบ QR เพื่อให้เห็นชัดตอนพิมพ์/เซฟรูป
                </div>

                <hr className="my-4" />

                <div className="mt-4">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                    <div>
                      <h5 className="mb-1 text-primary">BATCH QR</h5>
                      <div className="text-muted small">
                        ใส่หลายรหัสสินค้าในช่องเดียว ระบบจะสร้าง QR พร้อมราคาของทุกรหัสให้ในครั้งเดียว
                      </div>
                    </div>
                    <span className="badge text-bg-light border">Multi generate</span>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">PRODUCT CODES</label>
                    <textarea
                      className="form-control"
                      rows={5}
                      value={batchInput}
                      onChange={(e) => setBatchInput(e.target.value)}
                      placeholder={`PD1\nPD2\nPD5, PD8, PD10`}
                    />
                    <div className="form-text">
                      รองรับการคั่นด้วย comma หรือขึ้นบรรทัดใหม่ และถ้ากรอกรหัสซ้ำ ระบบจะสร้างป้ายซ้ำตามจำนวนที่กรอก
                    </div>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={batchLoading}
                      onClick={generateBatchLabels}
                    >
                      {batchLoading ? "กำลังโหลด QR..." : "โหลด QR ทั้งหมด"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={clearBatchLabels}
                    >
                      ล้างรายการ
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      onClick={downloadBatchPNGFromDOM}
                      disabled={batchLabels.length === 0}
                    >
                      ดาวน์โหลดทั้งหมดเป็น ZIP
                    </button>
                  </div>

                  {(batchLabels.length > 0 || batchNotFound.length > 0 || batchError) && (
                    <div className="mb-3">
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <span className="badge rounded-pill text-bg-success">
                          พบ {batchLabels.length} รายการ
                        </span>
                        <span className="badge rounded-pill text-bg-warning">
                          ไม่พบ {batchNotFound.length} รายการ
                        </span>
                      </div>

                      {batchError && (
                        <div className="alert alert-warning rounded-3 mb-2" role="alert">
                          {batchError}
                        </div>
                      )}

                      {batchNotFound.length > 0 && (
                        <div className="alert alert-warning rounded-3 mb-0" role="alert">
                          <div className="fw-semibold mb-1">ไม่พบรหัสสินค้า</div>
                          <div>{batchNotFound.join(", ")}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {batchLabels.length > 0 && (
                    <>
                      <div className="p-3 rounded-4" style={{ backgroundColor: "#ffffff" }}>
                        <div className="row g-3">
                          {batchLabels.map((item, index) => (
                            <div className="col-12 col-md-6" key={`${item.code}-${index}`}>
                              <div>
                                <div
                                  ref={(node) => {
                                    batchCardRefs.current[`${item.code}-${index}`] = node;
                                  }}
                                  className="d-inline-block"
                                >
                                  <QrCard code={item.code} price={item.price} />
                                </div>
                                <div className="small text-muted mt-1 px-1 text-truncate">
                                  {item.name}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="form-text mt-2">
                        ระบบจะสร้าง QR และราคาตามข้อมูลสินค้าที่พบในระบบสำหรับทุก code ที่คุณใส่
                      </div>
                    </>
                  )}
                </div>

                <div className="text-center mt-3">
                  <a href="/" className="btn btn-link text-decoration-none">
                    ← กลับหน้าเมนู
                  </a>
                </div>
              </div>
            </div>

            <div className="small text-muted mt-3 text-center">
              ทิป: ใช้ระดับแก้ความผิดพลาด (Error Correction) สูงขึ้นถ้าจะมีโลโก้ทับบางส่วน
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Modal: รายการสินค้า ---------- */}
      {showProductsModal && (
        <div
          className="modal fade show"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeProductsModal();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content rounded-4 shadow">
              <div className="modal-header">
                <h5 className="modal-title">รายการสินค้าทั้งหมด</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeProductsModal} />
              </div>

              <div className="modal-body">
                <div className="input-group mb-3">
                  <span className="input-group-text">ค้นหา</span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="พิมพ์โค้ด / ชื่อ / หน่วย"
                    value={productQuery}
                    onChange={(e) => setProductQuery(e.target.value)}
                    autoFocus
                  />
                </div>

                {loadingProducts && (
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลดรายการสินค้า…</span>
                  </div>
                )}
                {productsError && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    {productsError}
                  </div>
                )}

                <div className="table-responsive" style={{ maxHeight: 420 }}>
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: 72 }}>รูป</th>
                        <th style={{ width: 140 }}>โค้ด</th>
                        <th>ชื่อสินค้า</th>
                        <th style={{ width: 120 }} className="text-end">
                          ราคา
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((p) => (
                        <tr
                          key={p.code}
                          role="button"
                          onClick={() => chooseProduct(p)}
                          className="user-select-none"
                        >
                          <td>
                            {p.imageUrl ? (
                              <img
                                src={p.imageUrl}
                                alt={p.name}
                                width={56}
                                height={56}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  background: "#f5f5f5",
                                  cursor: "zoom-in",
                                  transition: "transform 0.2s ease",
                                }}
                                onMouseEnter={() => setPreviewImage(p.imageUrl ?? null)}
                                onMouseLeave={() => setPreviewImage(null)}
                                onError={(e) => {
                                  const img = e.currentTarget;
                                  img.onerror = null;
                                  img.src = "/no-image.png";
                                }}
                              />
                            ) : (
                              <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                            )}
                          </td>
                          <td className="fw-semibold">{p.code}</td>
                          <td>{p.name}</td>
                          <td className="text-end">
                            {p.price.toLocaleString("th-TH", { style: "currency", currency: "THB" })}
                          </td>
                        </tr>
                      ))}
                      {!loadingProducts && filteredProducts.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-3">
                            ไม่พบสินค้า
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {previewImage && (
                  <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{
                      backgroundColor: "rgba(0,0,0,0.7)",
                      zIndex: 2000,
                      pointerEvents: "none",
                    }}
                  >
                    <img
                      src={previewImage}
                      alt="preview"
                      style={{
                        maxWidth: "90%",
                        maxHeight: "90%",
                        borderRadius: 12,
                        boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                        background: "#fff",
                        transition: "transform 0.3s ease",
                        transform: "scale(1.05)",
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeProductsModal}>
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
